import { DatePipe, formatDate, NgClass, TitleCasePipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChip } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GetSelectedStruttura, UserProvider } from '@siat-fe/auth';
import { USER_VISIBILITY_PERMISSIONS } from '@siat-fe/core';
import { TranslateCellPipe } from '../../../../pipes/translate-cell.pipe';
import { WidgetDataStore } from '../../../../stores';
import { getTutelaAnonimatoInfo } from '../../../../utils/documenti-dse.utils';
import { IDynamicForm } from '../../../dynamic-form';
import { TableComponent, TableRow } from '../../../table';
import { TableCellDirective } from '../../../table/directive/table.cell.directive';
import { TableColumn } from '../../../table/models/table-column.model';
import { DashBoardWidgetId, TableWidget, TableWidgetData } from '../../models';
import { WidgetID } from '../../models/widget.enum';
import { Item } from '../../../form/select-field/select-field.component';
import { TipologicheService } from '../../../form/select-tipologica-field/select-tipologica-field.service';
import { forkJoin } from 'rxjs';

/**
 * Tipologica labels by id, used to render the referenti widget.
 */
interface ReferentiLabels {
  tipiReferente: Map<string, string>;
  tipiContatto: Map<string, string>;
}

/**
 * A table-widget row. The underlying domain shape varies per widget instance
 * (segnalazioni, PIC, terapie, documenti, parametri, ecc.), so this only
 * declares the fields this component itself reads across those domains.
 */
export interface TableWidgetRow extends TableRow {
  id?: string | number;
  listaPic?: { idModulo?: string; nomeModulo?: string }[];
  modulo?: { id?: string | number; nome?: string };
  idModulo?: string;
  nomeModulo?: string;
  statoPai?: string;
  risultatiScaleCaricate?: { dataCreazione: string; dynamicJson?: string }[];
  scalaPaziente?: {
    tipoScala?: { type?: string };
    idPaziente?: string | number;
  };
  prestazionePIC?: unknown;
  idPrestazionePrincipale?: string;
  paziente?: { id?: string | number; inTrasferimento?: unknown };
  nomeStruttura?: string;
  stato?: string;
  tipoParametro?: string;
  misurazione?: { massima?: number; minima?: number; value?: number };
  idFile?: { toString(): string } | string;
  fromMoniser?: boolean;
  caregiver?: boolean;
  idTipoReferentePaziente?: number | null;
  tipoDelega?: string | null;
  contattiReferenti?: {
    idTipoContattoReferentePaziente: number;
    contatto: string;
  }[];
  tipoReferente?: string;
  contattiConTipo?: ContattoCell[];
}

/**
 * A contatto of the referenti widget, ready to be rendered in the table cell.
 */
export interface ContattoCell {
  tipo: string;
  icon: 'call' | 'mail' | 'contact_page';
  contatto: string;
}

@Component({
  selector: 'siat-table-widget',
  imports: [
    TableComponent,
    TableCellDirective,
    NgClass,
    DatePipe,
    TitleCasePipe,
    MatIconModule,
    MatMenuTrigger,
    MatMenu,
    MatTooltipModule,
    TranslatePipe,
    TranslateCellPipe,
    MatIconButton,
    MatChip,
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
  ],
  templateUrl: './table-widget.component.html',
  styleUrl: './table-widget.component.scss',
})
export class TableWidgetComponent implements OnInit {
  private readonly statoByEsitoTrasferimento: Record<
    string,
    'ATTIVO' | 'CONCLUSO' | 'PROGRAMMATO'
  > = {
    Registrato: 'PROGRAMMATO',
    Trasferito: 'ATTIVO',
    'Chiusura Trasferimento': 'CONCLUSO',
  };

  @Input() widget!: TableWidget;

  @Output() needsDataRefresh = new EventEmitter<TableWidget>();
  @Output() changeElement = new EventEmitter<TableWidgetRow>();
  @Output() downloadElement = new EventEmitter<{ idDocumento: string; fromMoniser: boolean }>();
  @Output() addNotaSegnalazione = new EventEmitter<TableWidgetRow>();
  @Output() updateAllergiaIntolleranza = new EventEmitter<TableWidgetRow>();
  @Output() viewFoglioPresenzeApic = new EventEmitter<TableWidgetRow>();
  @Output() setAllergieNote = new EventEmitter<TableWidgetRow>();
  @Output() goToContatti = new EventEmitter<TableWidgetRow>();

  @Input() enforceStructureCheck = false;
  @Input() hasReadPermission = true;
  @Input() hasUpdatePermission = true;
  @Input() hasDeletePermission = true;
  @Input() lockedPatient: boolean;
  @Input() isApicActive = false;
  @Input() activeModulo?: { id: string | number; nome?: string };
  private router = inject(Router);
  private widgetDataStore = inject(WidgetDataStore);
  private translate = inject(TranslateService);
  private readonly tipologicheService = inject(TipologicheService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Tipologica labels of the referenti widget (loaded only for that widget).
   */
  private readonly referentiLabels = signal<ReferentiLabels>({
    tipiReferente: new Map(),
    tipiContatto: new Map(),
  });

  /**
   * Number of contatti shown in the referenti cell before the "+N" toggle.
   */
  readonly maxContattiVisibili = 2;

  /**
   * Ids of the referenti rows whose contatti list is expanded.
   */
  private readonly expandedContattiRows = signal(new Set<string | number>());

  /**
   * Inject UserProvider.
   */
  userProvider = inject(UserProvider);

  @Input()
  actionLabel: string;

  @Input()
  actionType: 'button' | 'link' = 'button';

  @Output()
  onClickAction = new EventEmitter<void>();

  @Output() goToDetailSegnalazioni = new EventEmitter<TableWidgetRow>();
  @Output() editBozza = new EventEmitter<TableWidgetRow>();
  @Output() goToDetailPic = new EventEmitter<TableWidgetRow>();
  @Output() sospendiTerapiaEvent = new EventEmitter<TableWidgetRow>();
  @Output() riattivaTerapiaEvent = new EventEmitter<TableWidgetRow>();
  @Output() terminaTerapiaEvent = new EventEmitter<TableWidgetRow>();
  @Output() addSomministrazioneEvent = new EventEmitter<TableWidgetRow>();
  @Output() storicoTerapiaEvent = new EventEmitter<TableWidgetRow>();
  @ViewChild('customContent', { static: true })
  customContent: TemplateRef<unknown>;

  expandedRows: string[] = [];
  readonly MAX_VISIBLE_PATIENTS = 2;

  dashboardWidgetIdEum = DashBoardWidgetId;
  /**
   * Returns the action label with total count appended
   * Only shows count for 'link' type actions (vedi tutte) that have a viewAll path
   */
  get actionLabelWithCount(): string {
    const count = this.tableData()?.totalCount;
    const hasViewAllPath = !!this.widget?.config?.path?.viewAll;
    const hideCount = [WidgetID.PRESCRIZIONI, WidgetID.DOCUMENTI_DSE].includes(
      this.widget?.id as WidgetID
    );

    // Only append count if actionType is 'link' and widget has viewAll path configured
    if (
      this.actionLabel &&
      this.actionType === 'link' &&
      hasViewAllPath &&
      !hideCount &&
      count !== undefined
    ) {
      return `${this.actionLabel} (${count})`;
    }
    return this.actionLabel || '';
  }

  get labels(): string[] {
    const columns = this.widget?.config?.columns;
    if (columns?.length) {
      return columns.map(col => col.key);
    }
    return this.widget?.config?.labels || [];
  }

  get fields(): string[] {
    const columns = this.widget?.config?.columns;
    if (columns?.length) {
      return columns.map(col => col.key);
    }
    return this.widget?.config?.fields || [];
  }

  get linkColumns(): string[] {
    return (this.widget?.config?.['linkColumns'] as string[]) || [];
  }

  get statusColumns(): string[] {
    let columns = (this.widget?.config?.['stato'] as string[]) || [];

    if (columns.length === 0 && this.labels.includes('stato')) {
      columns = ['stato'];
    }

    return columns;
  }

  get typeColumns(): string[] {
    const typeColumns = this.fields.filter((f: string) => f === 'type');

    if (typeColumns.length === 0 && this.labels.includes('type')) {
      return ['type'];
    }

    return typeColumns;
  }

  get translateColumns(): { key: string; prefix: string }[] {
    return (this.widget?.config?.columns || [])
      .filter(col => col.translate === true)
      .map(col => ({ key: col.key, prefix: col.translatePrefix || '' }));
  }

  tableData = computed(() => {
    const data = this.widgetDataStore.getWidgetData<TableWidgetData>(
      this.widget?.id
    );
    return (
      data || {
        columns: [],
        rows: [],
        totalCount: 0,
        currentPage: 0,
        pageSize: 10,
      }
    );
  });

  get tableColumns(): TableColumn[] {
    const configColumns = this.widget?.config?.columns || [];
    const data = this.tableData();

    // Primary source: config.columns (structured column definitions)
    if (configColumns.length > 0) {
      return configColumns.map(col => ({
        key: col.key,
        label: this.translate.instant(col.label) || col.label,
        sortable: col.sortable,
        filter: col.filter,
        sticky: col.sticky,
        width: col.width,
      }));
    }

    // Fallback: legacy labels/fields arrays
    const labels = this.widget?.config?.labels || [];
    const fields = this.widget?.config?.fields || [];
    if (labels.length > 0) {
      return labels.map((label, index) => ({
        key: fields[index] || label,
        label:
          this.translate.instant(`GENERAL.${label}`) ||
          label.charAt(0).toUpperCase() + label.slice(1),
        sortable: undefined,
        sticky: index === labels.length - 1 ? undefined : 'none',
        width: undefined,
      }));
    }

    // Fallback: API-provided columns
    return (
      data?.columns?.map(col => ({
        key: col.key,
        label: col.title,
        sortable: col.sortable !== false ? col.key : undefined,
        width: col.width,
      })) || []
    );
  }

  tableRows = computed(() => {
    const data = this.tableData();
    const rows = data?.rows || [];
    const dateCols = [
      ...this.dateOnlyColumns.map(k => ({ key: k, includeTime: false })),
      ...this.dateTimeColumns.map(k => ({ key: k, includeTime: true })),
    ];
    const isPaiWidget = this.widget?.id === WidgetID.PAI;
    const isReferentiWidget = this.widget?.id === WidgetID.REFERENTI;
    if (dateCols.length === 0 && !isPaiWidget && !isReferentiWidget)
      return rows;
    return rows.map(row => {
      const cloned = { ...row };
      dateCols.forEach(({ key, includeTime }) => {
        this.setValueByPath(
          cloned,
          key,
          this.formatDateCell(row, key, includeTime)
        );
      });
      if (isPaiWidget) {
        cloned['moduliPai'] = this.getPaiModuli(row);
      }
      if (isReferentiWidget) {
        Object.assign(cloned, this.getReferenteCells(row));
      }
      return cloned;
    });
  });

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    if (this.widget?.id === WidgetID.REFERENTI) {
      this.loadReferentiLabels();
    }
  }

  /**
   * Loads the tipi referente and tipi contatto used to label the referenti rows.
   */
  private loadReferentiLabels(): void {
    forkJoin({
      tipiReferente: this.tipologicheService.fetchTipologicaItems(
        'tipo_referente_paziente'
      ),
      tipiContatto: this.tipologicheService.fetchTipologicaItems(
        'tipo_contatto_referente_paziente'
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ tipiReferente, tipiContatto }) =>
        this.referentiLabels.set({
          tipiReferente: this.toLabelMap(tipiReferente),
          tipiContatto: this.toLabelMap(tipiContatto),
        })
      );
  }

  /**
   * Resolves the tipo referente label and the labelled contatti of a referente row.
   * @returns {Pick<TableWidgetRow, 'tipoReferente' | 'contattiConTipo'>} The display cells
   */
  private getReferenteCells(
    row: TableWidgetRow
  ): Pick<TableWidgetRow, 'tipoReferente' | 'contattiConTipo'> {
    const { tipiReferente, tipiContatto } = this.referentiLabels();
    return {
      tipoReferente:
        tipiReferente.get(String(row.idTipoReferentePaziente)) ??
        row.tipoDelega ??
        '—',
      contattiConTipo: (row.contattiReferenti ?? []).map(contatto =>
        this.toContattoCell(
          tipiContatto.get(String(contatto.idTipoContattoReferentePaziente)) ??
            '',
          contatto.contatto
        )
      ),
    };
  }

  /**
   * Picks the icon of a contatto from its tipo (telefono, mail or generic).
   * @returns {ContattoCell} The contatto cell
   */
  private toContattoCell(tipo: string, contatto: string): ContattoCell {
    const nome = tipo.toLowerCase();
    const icon = nome.includes('telefono')
      ? 'call'
      : nome.includes('mail')
        ? 'mail'
        : 'contact_page';
    return { tipo, icon, contatto };
  }

  /**
   * @returns {boolean} True if the contatti list of the row is expanded
   */
  isContattiExpanded(row: TableWidgetRow): boolean {
    return row.id != null && this.expandedContattiRows().has(row.id);
  }

  /**
   * Expands or collapses the contatti list of a referenti row.
   */
  toggleContatti(row: TableWidgetRow, event: Event): void {
    event.stopPropagation();
    if (row.id == null) return;
    const id = row.id;
    this.expandedContattiRows.update(ids => {
      const updated = new Set(ids);
      if (!updated.delete(id)) updated.add(id);
      return updated;
    });
  }

  /**
   * @returns {Map<string, string>} The item names by stringified id
   */
  private toLabelMap(items: Item[]): Map<string, string> {
    return new Map(items.map(item => [String(item.id), item.name]));
  }

  getPaiModuli(row: TableWidgetRow): string {
    const moduli = new Map<string, string>();
    const paiModuloId = this.getPaiModuloId(row);

    if (paiModuloId) {
      moduli.set(paiModuloId, this.getPaiModuloName(row));
    }

    row?.listaPic?.forEach(pic => {
      if (!pic?.idModulo) return;
      moduli.set(String(pic.idModulo), pic.nomeModulo ?? String(pic.idModulo));
    });

    return Array.from(moduli.values()).join(', ') || '-';
  }

  private getPaiModuloId(row: TableWidgetRow): string {
    return String(row?.modulo?.id ?? row?.idModulo ?? '');
  }

  private getPaiModuloName(row: TableWidgetRow): string {
    const id = this.getPaiModuloId(row);
    return row?.modulo?.nome ?? row?.nomeModulo ?? id;
  }

  private setValueByPath(
    obj: Record<string, unknown>,
    path: string,
    value: unknown
  ): void {
    const keys = path.split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...(current[keys[i]] as Record<string, unknown>) };
      current = current[keys[i]] as Record<string, unknown>;
    }
    current[keys[keys.length - 1]] = value;
  }

  hasDetails(): boolean {
    return !!this.widget.config.path?.detail;
  }

  /**
   * Restituisce l'elenco dei campi calcolati dell'ultima scala di un paziente
   */
  getUltimoRisultato(row: TableWidgetRow): { label: string; value: unknown }[] | null {
    const scaleCaricate = row?.risultatiScaleCaricate;
    const ultimaScala = scaleCaricate.reduce((prev, current) => {
      return new Date(current.dataCreazione) > new Date(prev.dataCreazione)
        ? current
        : prev;
    });

    if (
      !ultimaScala.dynamicJson ||
      typeof ultimaScala.dynamicJson !== 'string'
    ) {
      return null;
    }

    let parsed: IDynamicForm;
    try {
      parsed = JSON.parse(ultimaScala.dynamicJson);
    } catch {
      return null;
    }
    const firstField = parsed?.sections?.[0]?.fields?.[0];

    if (!firstField?.config?.computedFields) {
      return null;
    }
    return firstField.config.computedFields.map(cf => ({
      label: cf.label,
      value: firstField.value?.[cf.key] ?? 'N/A',
    }));
  }

  onLinkClick(row: TableWidgetRow, checkPrestazione?: boolean) {
    const isScaleWidget = this.widget.id === WidgetID.SCALEPAZIENTE;

    if (isScaleWidget) {
      const selectedScalaType = row?.scalaPaziente?.tipoScala?.type;
      const idPaziente = row?.scalaPaziente?.idPaziente;

      if (!selectedScalaType || !idPaziente) return;

      const detailPath = this.widget.config.path.detail;
      const url = `/home/${detailPath}/${selectedScalaType}/${idPaziente}`;

      this.router.navigateByUrl(url);
    } else if (this.widget.config?.path?.detail.includes('segnalazione')) {
      if (checkPrestazione) {
        if (row.prestazionePIC) {
          const picUrl = row.idPrestazionePrincipale
            ? `/home/pic/${row.idPrestazionePrincipale}/prestazione/${row.id}`
            : `/home/pic/${row?.id}`;
          this.router.navigateByUrl(picUrl, {
            state: {
              fromSchedaPazientePic: false,
              idPaziente: row.paziente.id,
            },
          });
          return;
        }
      }
      this.goToDetailSegnalazioni.emit(row);
    } else {
      const destination = `/home/${this.widget.config.path.detail}${row.id}`;
      this.router.navigateByUrl(destination, {
        state: { inTrasferimento: row?.paziente?.inTrasferimento ?? null },
      });
    }
  }

  expandRow(rowId: string): void {
    const index = this.expandedRows.indexOf(rowId);
    if (index > -1) {
      this.expandedRows.splice(index, 1);
    } else {
      this.expandedRows.push(rowId);
    }
  }
  isRowExpanded(rowId: string): boolean {
    return this.expandedRows.includes(rowId);
  }

  openContatti(row) {
    this.goToContatti.emit(row);
  }

  getDocumentoTypeLabel(value: string): string {
    if (!value) return '';

    if (value.includes('image')) {
      return this.translate.instant('DOCUMENTI.image');
    } else if (value.includes('pdf')) {
      return this.translate.instant('DOCUMENTI.pdf');
    } else {
      return this.translate.instant('DOCUMENTI.other');
    }
  }

  getMonitoraggioType(value: string): string {
    return this.translate.instant('PARAMETRI.' + value);
  }

  addType(value: TableWidgetRow): string {
    switch (value.tipoParametro) {
      case 'PRESSIONE_SANGUIGNA':
        return (
          value.misurazione.massima + '/' + value.misurazione.minima + ' mmHg'
        );
      case 'PESO':
        return value.misurazione.value + ' kg';
      case 'FREQUENZA_CARDIACA':
        return value.misurazione.value + ' bpm';
      case 'SATURAZIONE':
        return value.misurazione.value + '% SpO₂';
      case 'TEMPERATURA_CORPOREA':
        return value.misurazione.value + ' °C';
      case 'FREQUENZA_RESPIRATORIA':
        return value.misurazione.value + ' rpm';
      default:
        return String(value.misurazione.value);
    }
  }

  getStatusClass(status: string): string {
    if (!status) return '';

    const normalizedStatus = status.toLowerCase();

    let cssClass = '';
    switch (normalizedStatus) {
      case 'non_in_gestione':
      case 'in_gestione':
      case 'assegnato':
      case 'attivo':
      case 'aperto':
      case 'non assegnato':
        cssClass = 'status-aperta';
        break;
      case 'chiusura_forzata':
      case 'chiusura forzata':
      case 'creato':
        cssClass = 'status-chiusa';
        break;
      case 'completata':
      case 'chiusa':
      case 'chiuso':
        cssClass = 'status-completata';
        break;
      case 'sospesa':
      case 'sospeso':
        cssClass = 'status-sospesa';
        break;
      default:
        cssClass = `status-${normalizedStatus}`;
    }

    return cssClass;
  }

  getStatusAriaLabel(status: string): string {
    const statusLabel = this.translate.instant('GENERAL.stato');
    return `${statusLabel}: ${status}`;
  }

  getStatusLabel(status: string): string {
    if (!status) return '';
    const translated = this.translate.instant(
      'SEGNALAZIONE.STATUS.' + status.toUpperCase()
    );

    if (translated !== 'SEGNALAZIONE.STATUS.' + status.toUpperCase()) {
      return translated;
    }

    const genericKey = 'GENERAL.' + status.toLowerCase();
    const genericTranslated = this.translate.instant(genericKey);

    return genericTranslated !== genericKey
      ? genericTranslated
      : status.toUpperCase();
  }

  getApicStato(row: {
    esitoTrasferimento?: string | null;
  }): 'ATTIVO' | 'CONCLUSO' | 'PROGRAMMATO' {
    return (
      this.statoByEsitoTrasferimento[row?.esitoTrasferimento ?? ''] ??
      'PROGRAMMATO'
    );
  }

  getApicStatoLabel(row: { esitoTrasferimento?: string | null }): string {
    const stato = this.getApicStato(row);
    return this.translate.instant(`TRASFERIMENTO.STATO.${stato}`);
  }

  isApicEditable(row: { esitoTrasferimento?: string | null }): boolean {
    return this.getApicStato(row) !== 'CONCLUSO';
  }

  canManageApicTransfer(row: {
    idStrutturaOrigine?: number | string;
  }): boolean {
    const currentStructureId = GetSelectedStruttura()?.id;
    return (
      !!currentStructureId &&
      Number(row?.idStrutturaOrigine) === Number(currentStructureId)
    );
  }

  getChipsClass(status: string): string {
    if (!status) return '';

    const normalizedStatus = status.toLowerCase();

    let cssClass = '';
    switch (normalizedStatus) {
      case 'verde':
        cssClass = 'green-chips';
        break;
      case 'giallo':
        cssClass = 'orange-chips';
        break;
      case 'rosso':
        cssClass = 'red-chips';
        break;
      default:
        cssClass = `green-chips`;
    }

    return cssClass;
  }

  getAlertMessage(): string {
    const message = this.translate.instant(
      'GENERAL.' + this.widget?.config?.alertMessage
    );
    return message;
  }

  action(row, actionType?: 'edit' | 'delete' | boolean) {
    this.changeElement.emit({
      ...row,
      isDelete: actionType === 'delete' || actionType === true,
      actionType: actionType === true ? 'delete' : actionType,
    });
  }

  requestPaiAssociation(row: TableWidgetRow): void {
    this.changeElement.emit({
      ...row,
      actionType: 'requestPaiAssociation',
    });
  }

  canRequestPaiAssociation(row: TableWidgetRow): boolean {
    if (!this.activeModulo) return false;
    if (String(row?.statoPai ?? '').toLowerCase() === 'chiuso') return false;

    const activeModuloId = String(this.activeModulo.id);
    if (this.getPaiModuloId(row) === activeModuloId) return false;

    return !row?.listaPic?.some(pic => {
      return String(pic?.idModulo) === activeModuloId;
    });
  }

  openFoglioPresenzeApic(row) {
    this.viewFoglioPresenzeApic.emit(row);
  }

  download(row) {
    const downloadObj = {
      idDocumento: row.idFile.toString(),
      fromMoniser: row?.fromMoniser,
    };
    this.downloadElement.emit(downloadObj);
  }

  addNota(row) {
    this.addNotaSegnalazione.emit(row);
  }

  updateAllergiaInt(row) {
    this.updateAllergiaIntolleranza.emit(row);
  }

  hasFeaturePermission(permission: USER_VISIBILITY_PERMISSIONS): boolean {
    const hasPermission = this.userProvider.hasFeaturePermission(permission);
    return permission ? hasPermission : true;
  }

  /**
   * Returns the state of the segnalazione action button.
   * @param row The row of the table
   */
  getSegnalazioneActionState(row: TableWidgetRow): { disabled: boolean; tooltip: string } {
    const labelType =
      this.widget.id === 'segnalazioni'
        ? 'SEGNALAZIONI.singular'
        : 'PDC.singular';
    const defaultTooltip = this.translate.instant('CRUD.actions.details', {
      item: this.translate.instant(labelType),
    });

    if (this.widget.id !== 'segnalazioni' || !this.enforceStructureCheck) {
      return {
        disabled: false,
        tooltip: defaultTooltip,
      };
    }

    const currentStruttura = GetSelectedStruttura();
    const isSameStruttura = row.nomeStruttura === currentStruttura.nome;
    const hasPermission = this.userProvider.hasFeaturePermission(
      USER_VISIBILITY_PERMISSIONS.btn_avanzamento
    );
    const isCompleted = row.stato?.toUpperCase() === 'COMPLETATA';
    const canNavigate =
      (isSameStruttura && hasPermission) || (!isSameStruttura && isCompleted);

    if (canNavigate) {
      return { disabled: false, tooltip: defaultTooltip };
    }

    const errorTooltip = !isSameStruttura
      ? `Non si può ancora vedere il dettaglio finale perchè la segnalazione non è completata ed è in gestione presso: ${row.nomeStruttura}`
      : !hasPermission
        ? 'Non disponi dei permessi di avanzamento per questa segnalazione'
        : 'Azione non consentita';

    return { disabled: true, tooltip: errorTooltip };
  }

  getNotaSegnalazioneActionState(row: TableWidgetRow): {
    disabled: boolean;
    tooltip: string;
  } {
    const labelType = 'SEGNALAZIONE.singular';

    const defaultTooltip = this.translate.instant('SEGNALAZIONE.aggungi_nota', {
      item: this.translate.instant(labelType),
    });

    if (this.widget.id !== 'segnalazioni' || !this.enforceStructureCheck) {
      return {
        disabled: false,
        tooltip: defaultTooltip,
      };
    }

    const hasPermission = this.userProvider.hasFeaturePermission(
      USER_VISIBILITY_PERMISSIONS.btn_create_note
    );
    const isOpen =
      row.stato?.toUpperCase() === 'SOSPESA' ||
      row.stato?.toUpperCase() === 'IN_GESTIONE' ||
      row.stato?.toUpperCase() === 'NON_IN_GESTIONE';
    const canNavigate = hasPermission && isOpen;

    if (canNavigate) {
      return { disabled: false, tooltip: defaultTooltip };
    }

    const errorTooltip = !isOpen
      ? `Non si possono aggiungere note a una segnalazione chiusa o completata`
      : !hasPermission
        ? 'Non disponi dei permessi di avanzamento per questa segnalazione'
        : 'Azione non consentita';

    return { disabled: true, tooltip: errorTooltip };
  }

  private readonly DATE_ONLY_FIELDS = [
    'dataCreazione',
    'dataInserimento',
    'dataInizioValidita',
    'dataFineValidita',
    'dataInizio',
  ];
  private readonly DATE_TIME_FIELDS = [
    'dataInizioEvento',
    'dataApertura',
    'dataChiusura',
    'dataFineEvento',
    'dataErogazione',
    'dataAggiornamento',
  ];

  /**
   * Widget Esenzioni: una dataFineValidita dal 2100 in poi indica
   * un'esenzione senza scadenza e viene mostrata come "-".
   */
  private readonly ESENZIONE_SENZA_SCADENZA_DA_ANNO = 2100;

  private isEsenzioneSenzaScadenza(key: string, value: string): boolean {
    if (this.widget?.id !== WidgetID.ESENZIONI) return false;
    const field = key.split('.').pop() ?? key;
    if (field !== 'dataFineValidita') return false;
    // Supporta sia ISO (yyyy-MM-dd...) sia dd/MM/yyyy
    const year =
      /^(\d{4})-\d{2}-\d{2}/.exec(value)?.[1] ??
      /^\d{2}\/\d{2}\/(\d{4})/.exec(value)?.[1];
    return !!year && Number(year) >= this.ESENZIONE_SENZA_SCADENZA_DA_ANNO;
  }

  private getAllColumnKeys(): string[] {
    const columns = this.widget?.config?.columns;
    if (columns?.length) return columns.map(col => col.key);
    return this.widget?.config?.fields || [];
  }

  private showsDataCreazioneWithTime(): boolean {
    return (
      this.widget?.id === WidgetID.DIARIO_CLINICO ||
      this.widget?.id === WidgetID.SEGNALAZIONI
    );
  }

  get dateOnlyColumns(): string[] {
    const dataCreazioneHasTime = this.showsDataCreazioneWithTime();
    return this.getAllColumnKeys().filter(key => {
      const field = key.split('.').pop() ?? key;
      if (dataCreazioneHasTime && field === 'dataCreazione') return false;
      return this.DATE_ONLY_FIELDS.includes(field);
    });
  }

  get dateTimeColumns(): string[] {
    const dataCreazioneHasTime = this.showsDataCreazioneWithTime();
    return this.getAllColumnKeys().filter(key => {
      const field = key.split('.').pop() ?? key;
      if (dataCreazioneHasTime && field === 'dataCreazione') return true;
      return this.DATE_TIME_FIELDS.includes(field);
    });
  }

  formatDateCell(row: TableWidgetRow, key: string, includeTime = false): string {
    const value = this.getNestedValue(row, key) as string | undefined;
    if (!value) return '—';
    if (this.isEsenzioneSenzaScadenza(key, value)) return '-';
    try {
      const format = includeTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';
      return formatDate(value + 'Z', format, 'en-US');
    } catch {
      return value;
    }
  }

  getNestedValue(obj: TableWidgetRow, path: string): unknown {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Value of the first column; for terapie falls back to altroFarmaco when nomeFarmaco is missing.
   * @returns {unknown} The value to display in the first cell
   */
  getFirstCellValue(row: TableWidgetRow): unknown {
    const value = this.getNestedValue(row, this.labels[0]);
    if (this.widget?.id === WidgetID.TERAPIA && !value) {
      return row['altroFarmaco'];
    }
    return value;
  }

  protected readonly WidgetID = WidgetID;
  protected readonly getTutelaAnonimatoInfo = getTutelaAnonimatoInfo;

  private readonly formControlsByName = new Map<string, FormControl>();

  /**
   * Returns a cached FormControl for the given name, creating it on first
   * use. Reusing the same instance (instead of creating a new one on every
   * change-detection cycle, as calling `new FormControl()` directly from a
   * template would) avoids losing user interaction state and disabled-state
   * mismatches with the reactive forms directive.
   */
  createFormControl(name: string, value = false, disabled = false): FormControl {
    let control = this.formControlsByName.get(name);
    if (!control) {
      control = new FormControl({ value, disabled });
      this.formControlsByName.set(name, control);
    } else if (control.disabled !== disabled) {
      if (disabled) {
        control.disable();
      } else {
        control.enable();
      }
    }
    return control;
  }

  setSwitchData(row) {
    row.caregiver = !row.caregiver;
    this.action(row);
  }

  sospendiTerapia(row: TableWidgetRow): void {
    this.sospendiTerapiaEvent.emit(row);
  }

  riattivaTerapia(row: TableWidgetRow): void {
    this.riattivaTerapiaEvent.emit(row);
  }

  terminaTerapia(row: TableWidgetRow): void {
    this.terminaTerapiaEvent.emit(row);
  }

  addSomministrazione(row: TableWidgetRow): void {
    this.addSomministrazioneEvent.emit(row);
  }

  openStoricoTerapia(row: TableWidgetRow): void {
    this.storicoTerapiaEvent.emit(row);
  }
}
